import { ChangeEvent, useCallback, useRef, forwardRef, Ref, useState } from 'react';
import classnames from 'classnames';
import IconZoomIn from '@picsart/icons/IconZoomIn';
import IconZoomOut from '@picsart/icons/IconZoomOut';
import useClickOutside from '@picsart/rc/useClickOutside';

import { isZoomInEvent, isZoomOutEvent } from 'utils/common/hotKeyCombinations';
import { ZOOM_INPUT_CHANGE_VALUE } from 'configs/constants';

import useStyles from './styles';
import ZoomInOutButton, { Toggler } from './ZoomInOutButton';
import { IZoomInOutProps } from './types';
import testIds from './testIds';

const getInputValue = (e: ChangeEvent<HTMLInputElement>) => {
  const { value } = e.target;
  return Number(value.includes('%') ? value.replace('%', '') : value.slice(0, -1)) / 100;
};

const ZoomInOut = forwardRef(({
  onZoomIn, onZoomOut, inputOnBlur, inputOnChange, inputOnKeyPress, inputValue, toggle,
}: IZoomInOutProps, ref: Ref<HTMLDivElement>) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const zoomWrapperRef = useRef<HTMLDivElement>(null);

  useClickOutside(zoomWrapperRef, () => {
    setOpen(false);
  });

  const clicked = () => {
    inputRef.current?.select();
  };

  const handleInputOnChange = useCallback(e => inputOnChange(getInputValue(e)), [inputOnChange]);

  const inputChangeWithKeyboard = useCallback(e => {
    if (isZoomInEvent(e) || isZoomOutEvent(e)) {
      const val = getInputValue(e);
      e.stopPropagation();
      e.preventDefault();
      isZoomInEvent(e) ? inputOnChange(val + ZOOM_INPUT_CHANGE_VALUE) : inputOnChange(val - ZOOM_INPUT_CHANGE_VALUE);
    }
  }, [inputOnChange]);

  const getContent = () => {
    const content = (
      <>
        <ZoomInOutButton onClick={onZoomOut} icon={IconZoomOut} dataTestid={testIds.zoomInButton} />
        <label className={classes.label}>
          <input
            ref={inputRef}
            onClick={clicked}
            onBlur={inputOnBlur}
            onChange={handleInputOnChange}
            onKeyPress={inputOnKeyPress}
            onKeyDown={inputChangeWithKeyboard}
            className={classes.input}
            value={`${inputValue}%`}
            data-testid={testIds.zoomInputField}
          />
        </label>
        <ZoomInOutButton onClick={onZoomIn} icon={IconZoomIn} dataTestid={testIds.zoomOutButton} />
      </>
    );
    return toggle ? <div className={classes.withToggler} ref={zoomWrapperRef}>{content}</div> : content;
  };

  return (
    <div
      className={classnames(classes.root, {
        [classes.hasToggler]: toggle,
      })}
      data-testid={testIds.zoomInOutCmp}
      ref={ref}
    >
      {toggle && <Toggler onClick={() => setOpen(prev => !prev)} />}
      {(open || !toggle) && getContent()}
    </div>
  );
});

export default ZoomInOut;
